import { Entity, Property as IgnitionProperty } from '@square/ignition';
import PlusIcon from '@squareup/dex-icons/dex/action/Plus';
import { NativeButton } from '@squareup/dex-ui';
import { Markdown } from '@squareup/dex-ui-markdown';
import { Box, Paragraph20 } from '@squareup/dex-ui-shared-base';
import { commonIconStyles } from '@squareup/dex-ui-shared-icon-styles';
import { MarketButton } from '@squareup/dex-ui-shared-market';
import clsx from 'clsx';
import React, { FunctionComponent, useState } from 'react';

import { EntityDetails } from '../../EntityDetails/src/EntityDetails';

import styles from './property-content.module.css';

interface PropertyProps {
  property: IgnitionProperty;
  depth?: number | undefined;
  /**
   * When provided will callback with is showing details value when changed.
   */
  onShowingDetailsChange?: undefined | ((isShowing: boolean) => void);
  narrowView?: boolean;
  entityStack?: Entity[];
}

function metadataLabel(title: string, value: string) {
  return (
    <Box margin={{ right: '2x' }} className={styles['metadata-label']}>
      <Box margin={{ right: '1x' }}>
        <Paragraph20 as="span"> {title} </Paragraph20>
      </Box>
      <Paragraph20
        as="span"
        weight="medium"
        className={styles['metadata-label-value']}
      >
        {value}
      </Paragraph20>
    </Box>
  );
}

function metadataContainer(property: IgnitionProperty) {
  return (
    <Box margin={{ top: '1x' }} className={styles['metadata-container']}>
      {Boolean(property.minimum) &&
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        metadataLabel('Min', property.minimum!.toString())}
      {Boolean(property.maximum) &&
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        metadataLabel('Max', property.maximum!.toString())}
      {Boolean(property.minLength) &&
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        metadataLabel('Min Length', property.minLength!.toString())}
      {Boolean(property.maxLength) &&
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        metadataLabel('Max Length', property.maxLength!.toString())}
    </Box>
  );
}

/**
 */

// eslint-disable-next-line complexity
const PropertyContent: FunctionComponent<PropertyProps> = ({
  property,
  depth = 0,
  onShowingDetailsChange,
  narrowView = false,
  entityStack = [],
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const shouldShowMetadata =
    property.minimum ||
    property.maximum ||
    property.maxLength ||
    property.minLength;

  const metadata = shouldShowMetadata && metadataContainer(property);

  const shouldShowAttributuesButton =
    !showDetails && (Boolean(property.entity) || Boolean(property.itemEntity));

  const shouldShowDetailsArea = showDetails || shouldShowAttributuesButton;

  const description = property.readOnly
    ? `**Read only** ${property.description}`
    : property.description;

  const buttonText = <>Show {property.isEnum ? 'values' : 'attributes'}</>;
  const ariaLabel = `Show ${property.isEnum ? 'values' : 'attributes'} for ${
    property.name
  }`;
  const testId = `open-entity-details-${property.name}`;
  const trackingId = 'open-entity-details';
  const onClick = () => {
    onShowingDetailsChange && onShowingDetailsChange(!showDetails);
    setShowDetails(!showDetails);
  };

  const borderlessButton = (
    <NativeButton
      aria-label={ariaLabel}
      testId={testId}
      trackingId={trackingId}
      onClick={onClick}
      className={styles['narrow-button']}
    >
      <PlusIcon
        className={clsx(
          styles['button-blue'],
          commonIconStyles['icon-fill-current-color']
        )}
      />
      <Paragraph20
        weight="semi-bold"
        padding={{ right: '1x' }}
        className={styles['button-blue']}
      >
        {buttonText}
      </Paragraph20>
    </NativeButton>
  );

  const normalButton = (
    <MarketButton
      aria-label={ariaLabel}
      testId={testId}
      trackingId={trackingId}
      rank="secondary"
      size="small"
      onClick={onClick}
    >
      {buttonText}
    </MarketButton>
  );

  const showAttributesButton = narrowView ? borderlessButton : normalButton;
  const entity = (property.entity || property.itemEntity) as Entity;
  return (
    <Box>
      {description && (
        <Markdown markdown={description} textVariant="20"></Markdown>
      )}
      {metadata}
      {shouldShowDetailsArea && (
        <Box
          className={clsx(
            depth > 1 && showDetails && styles['nested-details-area']
          )}
          margin={!narrowView || showDetails ? { top: '2x' } : { top: '0.5x' }}
        >
          {shouldShowAttributuesButton && showAttributesButton}
          {showDetails && (
            <EntityDetails
              entity={entity}
              depth={depth + 1}
              onClose={() => {
                onShowingDetailsChange && onShowingDetailsChange(false);
                setShowDetails(false);
              }}
              narrowView={narrowView}
              entityStack={entityStack}
            ></EntityDetails>
          )}
        </Box>
      )}
    </Box>
  );
};

export { PropertyContent };
